const KEY = "clikkle.accounts.identityTransaction";
const TTL_MS = 15 * 60 * 1000;
const INTERNAL_ROUTES = new Set(["/security-center", "/security-enrollment"]);
const LEGACY_SESSION_KEYS = [
  "identity.clientId",
  "identity.protocolVersion",
  "identity.redirectUri",
  "referralApp",
  "clikkle.accounts.redirectto",
  "clikkle.accounts.postLoginPath",
];

const clean = (value) => (typeof value === "string" ? value.trim() : "");

function readRaw() {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(KEY) || "null");
    if (!value || Date.now() - Number(value.createdAt || 0) > TTL_MS) {
      window.sessionStorage.removeItem(KEY);
      return null;
    }
    return value;
  } catch {
    window.sessionStorage.removeItem(KEY);
    return null;
  }
}

export function getIdentityTransaction() {
  return readRaw();
}

export function clearLegacyIdentityState() {
  LEGACY_SESSION_KEYS.forEach((key) => window.sessionStorage.removeItem(key));
  if (typeof document !== "undefined") {
    document.cookie = "redirectto=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;SameSite=Lax;";
  }
}

export function clearIdentityTransaction() {
  window.sessionStorage.removeItem(KEY);
  clearLegacyIdentityState();
}

export function beginIdentityTransaction({ search = window.location.search, pathname = window.location.pathname } = {}) {
  clearLegacyIdentityState();

  const query = new URLSearchParams(search);
  const explicitClient = clean(query.get("client_id"));
  const explicitRedirect = clean(query.get("redirect_uri") || query.get("redirectto"));
  const explicitProtocol = clean(query.get("identity_protocol_version"));
  const referralApp = clean(query.get("referralApp"));
  const protectedReturn = INTERNAL_ROUTES.has(pathname) ? pathname : "";
  const existing = readRaw();
  const hasExplicitProductContext = Boolean(explicitClient || explicitRedirect || explicitProtocol || referralApp);
  const resetDirectLogin = !hasExplicitProductContext && !protectedReturn && pathname === "/login" && !existing?.internalReturnPath;

  if (resetDirectLogin) clearIdentityTransaction();
  const base = resetDirectLogin ? null : existing;
  const requestType = hasExplicitProductContext ? "product" : protectedReturn ? "internal" : base?.requestType || "direct";

  const transaction = requestType === "product" && hasExplicitProductContext
    ? {
        requestType,
        clientId: explicitClient,
        protocolVersion: explicitProtocol,
        redirectUri: explicitRedirect,
        referralApp,
        internalReturnPath: "",
        createdAt: Date.now(),
      }
    : {
        requestType,
        clientId: requestType === "internal" ? "accounts" : base?.clientId || "accounts",
        protocolVersion: requestType === "internal" ? "2" : base?.protocolVersion || "2",
        redirectUri: requestType === "internal" ? "" : base?.redirectUri || "",
        referralApp: base?.referralApp || "",
        internalReturnPath: protectedReturn || base?.internalReturnPath || "",
        createdAt: protectedReturn || !base ? Date.now() : base.createdAt,
      };

  window.sessionStorage.setItem(KEY, JSON.stringify(transaction));
  return transaction;
}

export function updateIdentityTransaction(patch) {
  const current = readRaw() || {
    requestType: "direct",
    clientId: "accounts",
    protocolVersion: "2",
    redirectUri: "",
    referralApp: "",
    internalReturnPath: "",
    createdAt: Date.now(),
  };
  const next = { ...current, ...patch, createdAt: current.createdAt || Date.now() };
  window.sessionStorage.setItem(KEY, JSON.stringify(next));
  return next;
}

export { KEY as IDENTITY_TRANSACTION_KEY, INTERNAL_ROUTES };
